/**
 * The rule editor's model: a discipline.yaml as packs to extend plus the settings the repo
 * sets itself. The visual controls cover the switches people change most; every other
 * setting in the file is kept as it is. Pure functions; the YAML written is the small,
 * safe subset the engine reads (block maps, flow lists, JSON-quoted strings).
 */
import { comparePackVersions, DISCIPLINE_PACKS, parsePackRef } from '@orgus/protocol';
import type { DisciplinePack, DisciplineRuleGroup } from '@orgus/protocol';

export interface EditorModel {
  /** Pack refs in order ('orgus/strict', 'acme/web@1.2.0'). */
  extends: string[];
  /** Section, then setting, as the file sets them (anything the editor does not know is kept). */
  settings: Record<string, Record<string, unknown>>;
}

export interface Curated {
  section: string;
  key: string;
  label: string;
  /** One line under the label. */
  hint: string;
  group: DisciplineRuleGroup;
  kind: 'bool' | 'number';
  /** The value in orgus/recommended, which every rulebook starts from. */
  def: boolean | number;
  /** Rule ids this setting turns on, for the dry run's per-rule rows. */
  rules: string[];
}

/** The switches the visual editor shows, grouped as the console groups rules. */
export const CURATED: readonly Curated[] = [
  { section: 'structure', key: 'unit_manifest', label: 'unit.yaml manifests', hint: 'Owner, layer, dependencies and public API per unit.', group: 'structure', kind: 'bool', def: false, rules: ['structure/unit-manifest', 'deps/undeclared'] },
  { section: 'structure', key: 'no_docs_in_src', label: 'No docs inside src/', hint: 'Markdown lives in docs/, never beside code.', group: 'structure', kind: 'bool', def: true, rules: ['structure/stray-doc'] },
  { section: 'dependencies', key: 'no_deep_imports', label: 'No deep imports', hint: 'Other units only through their entry point.', group: 'dependencies', kind: 'bool', def: true, rules: ['deps/deep-import'] },
  { section: 'tests', key: 'colocated_unit_tests', label: 'Units have tests', hint: 'Every unit with source files has a test file.', group: 'tests', kind: 'bool', def: true, rules: ['tests/missing'] },
  { section: 'docs', key: 'max_age_days', label: 'Docs go stale after (days)', hint: '0 never flags a doc as stale.', group: 'docs', kind: 'number', def: 180, rules: ['docs/stale'] },
  { section: 'design', key: 'tokens_only', label: 'Colours and spacing from tokens', hint: 'No raw hex, rgb or pixel spacing in UI code.', group: 'design', kind: 'bool', def: false, rules: ['design/raw-colour', 'design/px-spacing'] },
  { section: 'design', key: 'no_em_dash', label: 'No em dash in UI text', hint: 'Plain punctuation in every UI string.', group: 'design', kind: 'bool', def: false, rules: ['design/em-dash'] },
  { section: 'release', key: 'changeset_required', label: 'Changes carry a changeset', hint: 'A change to src/ needs a .changeset entry.', group: 'release', kind: 'bool', def: false, rules: ['release/changeset'] },
  { section: 'security', key: 'secrets', label: 'No secrets in files', hint: 'Keys, tokens and private keys are flagged.', group: 'security', kind: 'bool', def: false, rules: ['security/secret'] },
  { section: 'security', key: 'dangerous_calls', label: 'No eval or shell strings', hint: 'eval, new Function, shell=True and friends.', group: 'security', kind: 'bool', def: false, rules: ['security/dangerous-call'] },
  { section: 'security', key: 'lockfile', label: 'Dependencies are locked', hint: 'Every manifest has its lockfile committed.', group: 'security', kind: 'bool', def: false, rules: ['security/lockfile'] },
  { section: 'typescript', key: 'strict', label: 'TypeScript strict mode', hint: 'Every tsconfig runs strict.', group: 'typescript', kind: 'bool', def: false, rules: ['ts/strict'] },
  { section: 'typescript', key: 'no_explicit_any', label: 'No explicit any', hint: 'Warn on any in TypeScript source.', group: 'typescript', kind: 'bool', def: false, rules: ['ts/no-explicit-any'] },
  { section: 'typescript', key: 'no_ts_ignore', label: 'No @ts-ignore', hint: 'Silenced type errors are errors.', group: 'typescript', kind: 'bool', def: false, rules: ['ts/ts-ignore'] },
  { section: 'go', key: 'module', label: 'Go code in a module', hint: 'Every .go file sits under a go.mod.', group: 'go', kind: 'bool', def: false, rules: ['go/module'] },
  { section: 'go', key: 'no_panic', label: 'No panics in libraries', hint: 'Return errors outside main packages.', group: 'go', kind: 'bool', def: false, rules: ['go/panic'] },
  { section: 'python', key: 'project', label: 'pyproject.toml present', hint: 'Python code has a project file.', group: 'python', kind: 'bool', def: false, rules: ['py/project'] },
  { section: 'python', key: 'no_bare_except', label: 'No bare except', hint: 'Catch the exceptions you expect.', group: 'python', kind: 'bool', def: false, rules: ['py/bare-except'] },
  { section: 'waivers', key: 'max_days', label: 'Waivers last at most (days)', hint: 'Longer expiries are refused.', group: 'waivers', kind: 'number', def: 30, rules: ['waivers/invalid'] },
];

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The editor model of a parsed rulebook (or the recommended pack for none). */
export function modelFromRaw(raw: Record<string, unknown> | null | undefined): EditorModel {
  if (!raw) return { extends: ['orgus/recommended'], settings: {} };
  const ext = raw.extends;
  const list = typeof ext === 'string' ? [ext] : Array.isArray(ext) ? ext.filter((x): x is string => typeof x === 'string') : [];
  const settings: Record<string, Record<string, unknown>> = {};
  for (const [k, v] of Object.entries(raw)) if (k !== 'version' && k !== 'extends' && isObj(v)) settings[k] = { ...v };
  return { extends: list.map((r) => (r.includes('/') ? r : `orgus/${r}`)), settings };
}

/** Every pack the registry knows, newest version of each, built-ins first. */
export function packChoices(orgPacks: readonly DisciplinePack[]): DisciplinePack[] {
  const newest = new Map<string, DisciplinePack>();
  for (const p of orgPacks) {
    const cur = newest.get(p.id);
    if (!cur || comparePackVersions(p.version, cur.version) > 0) newest.set(p.id, p);
  }
  return [...DISCIPLINE_PACKS, ...[...newest.values()].sort((a, b) => a.id.localeCompare(b.id))];
}

function findPack(ref: string, orgPacks: readonly DisciplinePack[]): DisciplinePack | undefined {
  const r = parsePackRef(ref);
  if (!r) return undefined;
  const all = [...DISCIPLINE_PACKS, ...orgPacks].filter((p) => p.id === r.id).sort((a, b) => comparePackVersions(b.version, a.version));
  return r.version ? all.find((p) => comparePackVersions(p.version, r.version!) === 0) : all[0];
}

/**
 * The value each curated switch ends up with, and where it came from: the recommended
 * default, a pack, or the rulebook itself.
 */
export function effectiveSettings(m: EditorModel, orgPacks: readonly DisciplinePack[] = [], enforced: readonly string[] = []): Record<string, { value: boolean | number; from: string }> {
  const out: Record<string, { value: boolean | number; from: string }> = {};
  for (const c of CURATED) out[`${c.section}.${c.key}`] = { value: c.def, from: 'orgus/recommended' };
  const seen = new Set<string>();
  const apply = (ref: string, depth: number) => {
    const p = findPack(ref, orgPacks);
    if (!p || seen.has(p.id) || depth > 8) return;
    seen.add(p.id);
    for (const parent of p.extends ?? []) apply(parent, depth + 1);
    for (const c of CURATED) {
      const v = p.settings[c.section]?.[c.key];
      if (typeof v === typeof c.def) out[`${c.section}.${c.key}`] = { value: v as boolean | number, from: p.id };
    }
  };
  for (const ref of m.extends) apply(ref, 0);
  for (const c of CURATED) {
    const v = m.settings[c.section]?.[c.key];
    if (typeof v === typeof c.def) out[`${c.section}.${c.key}`] = { value: v as boolean | number, from: 'this rulebook' };
  }
  // The organisation's enforced packs apply on top of the rulebook, whatever it says.
  const before = { ...out };
  seen.clear();
  for (const ref of enforced) apply(ref, 0);
  for (const [k, e] of Object.entries(out)) if (e !== before[k]) out[k] = { ...e, from: `${e.from}, enforced` };
  return out;
}

/** Set one setting, or clear it (undefined) so the packs decide again. */
export function setSetting(m: EditorModel, section: string, key: string, value: unknown): EditorModel {
  const sec = { ...(m.settings[section] ?? {}) };
  if (value === undefined) delete sec[key];
  else sec[key] = value;
  const settings = { ...m.settings, [section]: sec };
  if (!Object.keys(sec).length) delete settings[section];
  return { ...m, settings };
}

export function togglePack(m: EditorModel, id: string, on: boolean): EditorModel {
  const has = m.extends.some((r) => parsePackRef(r)?.id === id);
  if (on === has) return m;
  if (!on) return { ...m, extends: m.extends.filter((r) => parsePackRef(r)?.id !== id) };
  // Base packs go first; focus and language packs after them.
  const base = DISCIPLINE_PACKS.find((p) => p.id === id)?.kind === 'base';
  return { ...m, extends: base ? [id, ...m.extends.filter((r) => DISCIPLINE_PACKS.find((p) => p.id === parsePackRef(r)?.id)?.kind !== 'base')] : [...m.extends, id] };
}

const PLAIN = /^[A-Za-z0-9_./@-]+$/;

function scalar(v: unknown): string {
  if (typeof v === 'string') return PLAIN.test(v) && !/^(?:true|false|null|yes|no|on|off|~|-?\d[\d.]*)$/i.test(v) ? v : JSON.stringify(v);
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v === null) return 'null';
  return JSON.stringify(v);
}

function flow(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(flow).join(', ')}]`;
  if (isObj(v)) return `{ ${Object.entries(v).map(([k, x]) => `${PLAIN.test(k) ? k : JSON.stringify(k)}: ${flow(x)}`).join(', ')} }`;
  return scalar(v);
}

/** Write the model as discipline.yaml. */
export function toYaml(m: EditorModel, header: readonly string[] = []): string {
  const out = [...header.map((h) => `# ${h}`), 'version: 1', `extends: ${flow(m.extends.length ? m.extends : ['orgus/recommended'])}`];
  for (const [section, values] of Object.entries(m.settings)) {
    const entries = Object.entries(values);
    if (!entries.length) continue;
    out.push('', `${section}:`);
    for (const [k, v] of entries) out.push(`  ${PLAIN.test(k) ? k : JSON.stringify(k)}: ${flow(v)}`);
  }
  return `${out.join('\n')}\n`;
}

/** The model as an org pack's settings (for "Publish as a pack"). */
export function packFromModel(m: EditorModel, id: string, version: string, title: string, description: string): Omit<DisciplinePack, 'kind'> & { kind: 'org' } {
  return { id, version, title, description, kind: 'org', extends: m.extends.filter((r) => r !== 'orgus/recommended'), settings: m.settings };
}
